import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import ModalBasicLayout from "./ModalBasicLayout";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import {
  useImageEdit,
  useChatMessages,
  useImageLayers,
  useAnalyzeImageLayers,
  PROMPTS_CHECKPOINT_SIZE,
} from "./ModalImageEdit/hooks";
import { buildEditImagePromptsForJob } from "./ModalImageEdit/hooks/imageEditCheckpoint";
import {
  ChatSidebar,
  ImageEditTopBar,
  ResponsiveSidebarContainer,
  WorkModeSelector,
} from "./ModalImageEdit/shared";
import { ImageCanvas, SingleChatMessages } from "./ModalImageEdit/single";
import LayerCanvas from "./ModalImageEdit/layers/LayerCanvas";
import LayersSidebar from "./ModalImageEdit/layers/LayersSidebar";
import ObjectEraserTool from "./ModalImageEdit/layers/ObjectEraserTool";
import {
  createEraseMaskFile,
  canApplyEraseLayerObjectResult,
} from "./ModalImageEdit/layers/eraseLayerObject";
import { OBJECT_ERASER_BRUSH_FRACTION_DEFAULT } from "./ModalImageEdit/layers/ObjectEraserOverlay";
import InstancePropertiesPanel from "./ModalCreativeTemplateEdit/InstancePropertiesPanel";
import useTextHighlightSelection, {
  applyIfHighlightField,
} from "../../features/creativeTemplates/editor/useTextHighlightSelection";
import { CanvasToolsBar } from "../Editor/CanvasToolsBar";
import {
  IMAGE_EDIT_WORK_MODE,
  LAYER_TYPE,
  getLayerType,
  layerToInspectorItem,
  pixelsToNormalized,
} from "./ModalImageEdit/layers/layerGeometry";
import { confirmAnalyzeDetectsText } from "../../utils/confirmAnalyzeDetectsText";
import { getImageDimensionsFromUrl } from "../../features/creativeTemplates/utils/getImageDimensionsFromFile";
import { isLayerizeImageTooSmall } from "../../features/creativeTemplates/utils/layerizeImageConstraints";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startGetBrandInformation } from "../../actions/adsCampaigns";
import {
  startEraseLayerObject,
  startSaveEditedLayeredImage,
} from "../../actions/imageLayers";
import { getIsBlockedActions } from "../../actions/getters";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  startListenerMergedFonts,
  stopListenerMergedFonts,
} from "../../actions/fonts";
import { loadFontFaces } from "../../features/creativeTemplates/utils/fonts";
import { blurActiveEditable } from "../../features/creativeTemplates/utils/canvasClipboard";
import {
  listStoredInlineReplacements,
  urlsFromInlineReplacementAssetRefs,
} from "../../features/creativeTemplates/utils/textInlineReplacements";
import { useResizableSidebarWidth } from "../../features/creativeTemplates/editor/useResizableSidebarWidth";
import { alignSelectedBoxes } from "../../features/creativeTemplates/editor/alignSelectedLayers";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { validateModalImageEditTextFields } from "../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import {
  BUTTON_CLOSE,
  BUTTON_CONTINUE,
  BUTTON_GO_BACK,
  MODAL_IMAGE_EDIT_TITLE,
  MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_TITLE,
  MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_DESCRIPTION,
  MODAL_IMAGE_EDIT_CLOSE_TITLE,
  MODAL_IMAGE_EDIT_CLOSE_DESCRIPTION,
  TEMPLATES_ANALYZE_IMAGE_TOO_SMALL,
  TEMPLATES_KEEP_EDITING,
  TEMPLATES_PRODUCE_ADD_TEXT,
  TEMPLATES_PRODUCE_SAVE_ERROR,
  CANVAS_TOOLS_BAR_LABEL,
  WARNING,
} from "../../i18n/keysTranslations";
import { UploadFilesContext } from "../../contexts/UploadFilesContext";

import { LaunchError } from "../../utils/errors";

/**
 * Main modal for AI-assisted image editing
 */
const ModalImageEdit = ({
  isOpen,
  onClose,
  initialImageUrl = null,
  initialImageEditSession = null,
  onCallbackSave = () => {},
  showSaveAlert = true,
  initialWorkMode = IMAGE_EDIT_WORK_MODE.GENERATIVE,
  jobID,
  alertStatus,
  generationData,
  contentID,
  isCarousel = false,
  carouselIndex,
}) => {
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const theme = useTheme();
  const { t } = useTranslationApp();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatchApp();
  const { uploadFile } = useContext(UploadFilesContext) || {};
  const { userID } = dispatch(getPathDatabaseByRole());
  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );

  const resolvedInitialImageUrl =
    initialImageUrl || generationData?.sourceUrl || generationData?.url || null;

  const [imageUrl, setImageUrl] = useState(resolvedInitialImageUrl);
  /** Base image URL for the current checkpoint block (sent to the API as `url`) */
  const [blockBaseImageUrl, setBlockBaseImageUrl] = useState(
    resolvedInitialImageUrl
  );
  /** Turn objects accumulated since the last checkpoint (successful edits only) */
  const [checkpointPromptsHistory, setCheckpointPromptsHistory] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const {
    width: layersSidebarWidth,
    onResizePointerDown: onResizeLayersSidebar,
  } = useResizableSidebarWidth({
    defaultWidth: 420,
    minWidth: 280,
    maxWidth: 640,
  });
  const [businessLookAndFeel, setBusinessLookAndFeel] = useState("");
  const [workMode, setWorkMode] = useState(
    initialWorkMode || IMAGE_EDIT_WORK_MODE.GENERATIVE
  );
  const [editingTextID, setEditingTextID] = useState(null);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const [isObjectEraserActive, setIsObjectEraserActive] = useState(false);
  const [objectEraserBrushFraction, setObjectEraserBrushFraction] = useState(
    OBJECT_ERASER_BRUSH_FRACTION_DEFAULT
  );
  const [hasObjectEraserMask, setHasObjectEraserMask] = useState(false);
  const [erasingLayerIds, setErasingLayerIds] = useState({});
  const objectEraserLayerIdRef = useRef(null);
  const pendingErasesRef = useRef(new Map());
  const layersRef = useRef([]);
  const isOpenRef = useRef(isOpen);
  const eraseSessionRef = useRef(0);
  const fontListenerRefs = useRef({ commonRef: null, advertiserRef: null });
  const fontFamiliesRef = useRef([]);
  const brandFontFamilyRef = useRef("");
  const lastSessionHydrateKeyRef = useRef(null);
  isOpenRef.current = isOpen;

  const {
    chatMessages,
    addMessage,
    replaceThinkingMessage,
    chatScrollRef,
    setChatMessages,
  } = useChatMessages();
  const { isProcessing, isEditing, editImage, createThinkingMessage } =
    useImageEdit();
  const isEditJobInProgress = Boolean(initialImageEditSession?.isJobInProgress);
  const isChatProcessing = isProcessing || isEditJobInProgress;
  const isCanvasEditing = isEditing || isEditJobInProgress;
  const isLayersMode = workMode === IMAGE_EDIT_WORK_MODE.LAYERS;
  const {
    layers,
    canvasWidth,
    canvasHeight,
    layersDirty,
    selectedLayerId,
    selectedLayerIds,
    selectLayer,
    selectLayers,
    updateLayerBox,
    updateLayerBoxes,
    addLayersFromUrls,
    addTextLayer,
    updateLayerText,
    updateLayerStyle,
    replaceLayerImage,
    deleteLayer,
    duplicateLayer,
    reorderLayers,
    renameLayer,
    toggleLayerLock,
    toggleLayerVisible,
    persistSnapshotNow,
    hasSnapshot,
    isLoadingSnapshot,
  } = useImageLayers({
    sourceUrl: imageUrl,
    enabled: isOpen,
    hotkeysEnabled: isOpen && isLayersMode,
    fontFamiliesRef,
    brandFontFamilyRef,
  });
  layersRef.current = layers;
  const acknowledgedLayerLossForUrlRef = useRef(null);
  const isEnteringLayersModeRef = useRef(false);
  const resolvedContentID = contentID || generationData?.contentID || null;
  const resolvedIsCarousel = Boolean(isCarousel || generationData?.isCarousel);
  const resolvedCarouselIndex = carouselIndex ?? generationData?.carouselIndex;

  const { analyze, isAnalyzing, errorMessage } = useAnalyzeImageLayers({
    sourceUrl: imageUrl,
    alertStatus,
    jobID,
    contentID: resolvedContentID,
    isCarousel: resolvedIsCarousel,
    carouselIndex: resolvedCarouselIndex,
  });

  const onAnalyzeLayers = useCallback(
    async ({ onUsageConfirmed } = {}) => {
      try {
        const dimensions = await getImageDimensionsFromUrl({ url: imageUrl });
        if (isLayerizeImageTooSmall(dimensions)) {
          SimpleAlert({
            title: t(WARNING),
            text: t(TEMPLATES_ANALYZE_IMAGE_TOO_SMALL),
            icon: "error",
          });
          return { ok: false };
        }
      } catch {
        // Continue to confirmation if image dimensions cannot be read.
      }

      const confirmation = await confirmAnalyzeDetectsText({ t });
      if (!confirmation.proceed) return { ok: false };
      return analyze({
        extractText: confirmation.extractText,
        onUsageConfirmed,
      });
    },
    [analyze, imageUrl, t]
  );

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const currentImageHasLayerAnalysis = hasSnapshot || layers.length > 0;

  const selectedLayer = useMemo(
    () => layers.find((layer) => layer.id === selectedLayerId) || null,
    [layers, selectedLayerId]
  );
  const {
    textSelection,
    keepTextEditingRef,
    onTextSelectionChange,
    styleForHighlightField,
    isTextRangeSelected,
    selectedPhrase,
  } = useTextHighlightSelection({
    selectedID: selectedLayerId,
    editingTextID,
    canvasSelector: "[data-image-layers-canvas]",
    nodesVersion: layers,
    getNode: (layerId) => layers.find((layer) => layer.id === layerId) || null,
    getNodeText: (layer) => String(layer?.text || ""),
    // Image layers carry no source kind, so being a text layer is enough.
    canHighlight: (layer) =>
      Boolean(layer) && getLayerType(layer) === LAYER_TYPE.TEXT,
    persistHighlights: ({ nodeID, highlights }) => {
      updateLayerStyle(nodeID, {
        highlights:
          Array.isArray(highlights) && highlights.length ? highlights : null,
      });
    },
  });

  const selectedInspectorItem = useMemo(
    () =>
      layerToInspectorItem({
        layer: selectedLayer,
        canvasWidth,
        canvasHeight,
      }),
    [canvasHeight, canvasWidth, selectedLayer]
  );

  const exitObjectEraser = useCallback(() => {
    objectEraserLayerIdRef.current = null;
    setIsObjectEraserActive(false);
    setHasObjectEraserMask(false);
  }, []);

  const onSelectLayer = (layerId, event, options) => {
    blurActiveEditable(event);
    if (isObjectEraserActive && layerId !== objectEraserLayerIdRef.current) {
      exitObjectEraser();
    }
    selectLayer(layerId, event, options);
  };

  const onSelectLayers = (ids, event) => {
    blurActiveEditable(event);
    if (isObjectEraserActive) {
      exitObjectEraser();
    }
    selectLayers(ids, event);
  };

  const onAlignLayers = (align) => {
    const ids = selectedLayerIds.filter(Boolean);
    if (ids.length < 2) return;
    const selectedBoxes = layers
      .filter(
        (layer) =>
          ids.includes(layer.id) && !layer.isBackground && !layer.locked
      )
      .map((layer) => ({
        id: layer.id,
        x: layer.x,
        y: layer.y,
        width: layer.width,
        height: layer.height,
      }));
    if (selectedBoxes.length < 2) return;
    updateLayerBoxes(
      alignSelectedBoxes({ boxes: selectedBoxes, align }).map((box) => ({
        id: box.id,
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height,
      }))
    );
  };

  const onToggleObjectEraser = () => {
    if (isObjectEraserActive) {
      exitObjectEraser();
      return;
    }
    if (!selectedLayerId || erasingLayerIds[selectedLayerId]) return;
    objectEraserLayerIdRef.current = selectedLayerId;
    setIsObjectEraserActive(true);
  };

  const onEraseObject = async (request) => {
    if (!request?.layerUrl || !request?.mask?.blob || !request?.layerId) {
      return;
    }
    if (typeof uploadFile !== "function") {
      return;
    }
    if (pendingErasesRef.current.has(request.layerId)) {
      return;
    }

    const layerId = request.layerId;
    const layerUrl = request.layerUrl;
    const session = eraseSessionRef.current;
    pendingErasesRef.current.set(layerId, { layerUrl, session });
    setErasingLayerIds((current) => ({ ...current, [layerId]: true }));
    exitObjectEraser();

    try {
      const maskUrl = await uploadFile({
        file: createEraseMaskFile(request.mask.blob, request.mask.mimeType),
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (!maskUrl) {
        return;
      }

      const response = await dispatch(
        startEraseLayerObject({
          imageUrl: layerUrl,
          maskUrl,
          preserveAlpha: !request.isBackground,
          layerId,
        })
      );

      const pending = pendingErasesRef.current.get(layerId);
      if (!pending) return;
      if (!isOpenRef.current) return;
      if (pending.session !== eraseSessionRef.current) return;
      if (
        !response?.ok ||
        !response?.data?.url ||
        !canApplyEraseLayerObjectResult({
          layerId,
          layerUrl: pending.layerUrl,
          layers: layersRef.current,
        })
      ) {
        return;
      }
      replaceLayerImage(layerId, response.data.url, { preserveSize: true });
    } finally {
      pendingErasesRef.current.delete(layerId);
      setErasingLayerIds((current) => {
        if (!current[layerId]) return current;
        const next = { ...current };
        delete next[layerId];
        return next;
      });
    }
  };

  const confirmAiEditLosesLayers = async () => {
    if (!currentImageHasLayerAnalysis) return true;
    if (acknowledgedLayerLossForUrlRef.current === imageUrl) return true;

    const result = await ConfirmAlert({
      title: t(MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_TITLE),
      text: t(MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_DESCRIPTION),
      confirmButtonText: t(BUTTON_CONTINUE),
      cancelButtonText: t(BUTTON_GO_BACK),
      icon: "warning",
    });

    if (!result?.isConfirmed) return false;
    acknowledgedLayerLossForUrlRef.current = imageUrl;
    return true;
  };

  const onDeleteLayer = (layerId) => {
    if (editingTextID && (!layerId || editingTextID === layerId)) {
      setEditingTextID(null);
    }
    if (
      isObjectEraserActive &&
      (!layerId || layerId === objectEraserLayerIdRef.current)
    ) {
      exitObjectEraser();
    }
    deleteLayer(layerId);
  };

  const onWorkModeChange = async (nextMode) => {
    if (nextMode === workMode || isEnteringLayersModeRef.current) return;
    if (isAnalyzing && nextMode === IMAGE_EDIT_WORK_MODE.GENERATIVE) {
      return;
    }
    if (
      nextMode === IMAGE_EDIT_WORK_MODE.GENERATIVE &&
      workMode === IMAGE_EDIT_WORK_MODE.LAYERS
    ) {
      const canContinue = await confirmAiEditLosesLayers();
      if (!canContinue) return;
    }
    if (nextMode === IMAGE_EDIT_WORK_MODE.LAYERS) {
      const needsAnalysis =
        !currentImageHasLayerAnalysis && !isAnalyzing && !isLoadingSnapshot;
      if (needsAnalysis) {
        isEnteringLayersModeRef.current = true;
        void onAnalyzeLayers({
          onUsageConfirmed: () => {
            isEnteringLayersModeRef.current = false;
            setWorkMode(IMAGE_EDIT_WORK_MODE.LAYERS);
          },
        }).finally(() => {
          isEnteringLayersModeRef.current = false;
        });
        return;
      }
    }
    setWorkMode(nextMode);
    if (nextMode !== IMAGE_EDIT_WORK_MODE.LAYERS) {
      setEditingTextID(null);
      exitObjectEraser();
    }
  };

  const onAddTextLayer = () => {
    addTextLayer({ name: t(TEMPLATES_PRODUCE_ADD_TEXT) });
    setEditingTextID(null);
  };

  const onChangeField = ({ key, value }) => {
    if (!selectedLayerId) return false;
    const highlightStyle = styleForHighlightField({
      node: selectedLayer,
      nodeID: selectedLayerId,
      key,
      value,
    });
    if (
      applyIfHighlightField(highlightStyle, (style) => {
        updateLayerStyle(selectedLayerId, {
          highlights: style.highlights ?? null,
        });
      })
    ) {
      return;
    }
    if (key === "highlights") {
      const nextHighlights = Array.isArray(value) ? value : [];
      updateLayerStyle(selectedLayerId, {
        highlights: nextHighlights.length ? nextHighlights : null,
      });
      return;
    }
    if (key === "inlineReplacements") {
      const nextReplacements = Array.isArray(value) ? value : [];
      updateLayerStyle(selectedLayerId, {
        inlineReplacements: nextReplacements.length ? nextReplacements : null,
      });
      return;
    }
    switch (key) {
      case "name":
        renameLayer(selectedLayerId, value);
        return true;
      case "fixedValue":
        updateLayerText(selectedLayerId, value);
        return true;
      case "fontSelection":
        updateLayerStyle(
          selectedLayerId,
          value && typeof value === "object" ? value : {}
        );
        return;
      case "fontAssetRef":
      case "fontFaceRef":
      case "fontWeight":
      case "fontSize":
      case "lineHeight":
      case "letterSpacing":
      case "strokeWidth":
      case "strokeColor":
      case "shadowColor":
      case "shadowAngle":
      case "shadowDistance":
      case "shadowBlur":
      case "backgroundPadding":
      case "backgroundRadius":
      case "backgroundColor":
      case "color":
      case "align":
      case "orientation":
      case "textStyle":
        updateLayerStyle(selectedLayerId, { [key]: value });
        return;
      case "fill":
      case "radius":
        return;
      default: {
        const exhaustive = key;
        throw new Error(`Unhandled property: ${exhaustive}`);
      }
    }
  };

  const onChangeBox = ({ x, y, width: nextWidth, height: nextHeight }) => {
    if (!selectedLayer || !canvasWidth || !canvasHeight) return;
    const current = selectedInspectorItem?.box;
    if (!current) return;
    updateLayerBox(
      selectedLayerId,
      pixelsToNormalized({
        x: x ?? current.x,
        y: y ?? current.y,
        width: nextWidth ?? current.width,
        height: nextHeight ?? current.height,
        displayWidth: canvasWidth,
        displayHeight: canvasHeight,
      })
    );
  };

  const onUploadAsset = async ({ file }) => {
    if (!file || typeof uploadFile !== "function" || !selectedLayerId) return;
    setIsUploadingAsset(true);
    try {
      const uploadedUrl = await uploadFile({
        file,
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (uploadedUrl) {
        replaceLayerImage(selectedLayerId, uploadedUrl);
      }
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const selectedInlineReplacementUrls = useMemo(
    () =>
      urlsFromInlineReplacementAssetRefs(
        selectedLayer?.style?.inlineReplacements
      ),
    [selectedLayer]
  );

  const onUploadInlineReplacementAsset = async ({ file, slotId }) => {
    if (
      !file ||
      !slotId ||
      typeof uploadFile !== "function" ||
      !selectedLayerId
    ) {
      return;
    }
    setIsUploadingAsset(true);
    try {
      const uploadedUrl = await uploadFile({
        file,
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (!uploadedUrl) return;
      const layer =
        layersRef.current.find((item) => item.id === selectedLayerId) || null;
      const slots = listStoredInlineReplacements(
        layer?.style?.inlineReplacements
      );
      if (!slots.some((slot) => slot.id === slotId)) return;
      updateLayerStyle(selectedLayerId, {
        inlineReplacements: slots.map((slot) =>
          slot.id === slotId ? { ...slot, assetRef: uploadedUrl } : slot
        ),
      });
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const onTextCommit = (layerId, value) => {
    updateLayerText(layerId, value);
    // Clicking the properties panel blurs the editor: keep the edit open so
    // the phrase stays selected while its style is being changed.
    const keepEditing = keepTextEditingRef.current;
    keepTextEditingRef.current = false;
    if (!keepEditing) setEditingTextID(null);
  };

  /**
   * Gets brand information including businessLookAndFeel
   */
  const getBrandInformation = async () => {
    const response = await dispatch(startGetBrandInformation());
    if (response.ok) {
      const { data } = response;
      setBusinessLookAndFeel(data?.businessLookAndFeel || "");
      brandFontFamilyRef.current = data?.businessTypography?.fontFamily || "";
    }
  };

  /**
   * Handles sending a message to edit the image
   */
  const onSendMessage = async (
    prompt,
    attachmentUrls = [],
    attachmentFileNames = {}
  ) => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || isChatProcessing || !blockBaseImageUrl) return;
    try {
      validateModalImageEditTextFields({
        prompt: trimmedPrompt,
        isPlanActive,
        isTrial,
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onSendMessage",
          capture: "exception",
        })
      );
      addMessage({
        id: Date.now().toString(),
        role: "assistant",
        content: `Error: ${error?.message || "The request could not be processed."}`,
        timestamp: new Date(),
        isError: true,
      });
      return;
    }

    const canContinue = await confirmAiEditLosesLayers();
    if (!canContinue) return;

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: 1,
      })
    );
    if (isBlocked) return;

    const resolvedAttachmentUrls = Array.isArray(attachmentUrls)
      ? attachmentUrls
      : [];

    const promptsOrdered = buildEditImagePromptsForJob({
      history: checkpointPromptsHistory,
      prompt: trimmedPrompt,
      attachmentUrls: resolvedAttachmentUrls,
    });

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: trimmedPrompt,
      timestamp: new Date(),
      imageUrl: imageUrl,
      attachmentUrls: resolvedAttachmentUrls,
      attachmentFileNames: attachmentFileNames || {},
    };

    addMessage(userMessage);

    const thinkingMessage = createThinkingMessage();
    addMessage(thinkingMessage);

    await editImage(
      blockBaseImageUrl,
      promptsOrdered,
      businessLookAndFeel,
      // onSuccess
      (newImageUrl, aiResponse) => {
        setImageUrl(newImageUrl);
        replaceThinkingMessage(aiResponse);

        const completedTurns = promptsOrdered.map((turn, index) =>
          index === promptsOrdered.length - 1
            ? { ...turn, resultUrl: newImageUrl }
            : turn
        );
        const completedBlockSize = completedTurns.length;

        if (completedBlockSize >= PROMPTS_CHECKPOINT_SIZE) {
          setBlockBaseImageUrl(newImageUrl);
          setCheckpointPromptsHistory([]);
        } else {
          setCheckpointPromptsHistory(completedTurns);
        }
      },
      // onError
      (errorMessageText) => {
        replaceThinkingMessage(errorMessageText);
      }
    );
  };

  /**
   * Handles selecting an image from messages
   */
  const onImageSelect = (selectedImageUrl) => {
    setImageUrl(selectedImageUrl);
  };

  const onAddLayerUrls = async (urls) => {
    await addLayersFromUrls(urls);
  };

  const isCloseConfirmOpenRef = useRef(false);

  const onRequestClose = async (reason) => {
    if (reason === "escapeKeyDown") {
      if (isObjectEraserActive) {
        exitObjectEraser();
        return false;
      }
      if (editingTextID) return false;
    }
    if (isCloseConfirmOpenRef.current) return false;

    isCloseConfirmOpenRef.current = true;
    try {
      const result = await ConfirmAlert({
        title: t(MODAL_IMAGE_EDIT_CLOSE_TITLE),
        text: t(MODAL_IMAGE_EDIT_CLOSE_DESCRIPTION),
        confirmButtonText: t(BUTTON_CLOSE),
        cancelButtonText: t(TEMPLATES_KEEP_EDITING),
        icon: "warning",
      });
      if (!result?.isConfirmed) return false;
      onClose();
    } finally {
      isCloseConfirmOpenRef.current = false;
    }
  };

  const onSaveLayeredComposition = async (payload) => {
    const result = await dispatch(
      startSaveEditedLayeredImage({
        layers,
        canvasWidth,
        canvasHeight,
      })
    );
    const uploadedUrl = result?.data?.url;
    if (!result?.ok || !uploadedUrl) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
        icon: "error",
      });
      throw new Error(result?.message || "Failed to save layered image");
    }
    await persistSnapshotNow(uploadedUrl);
    await onCallbackSave({ ...payload, url: uploadedUrl });
  };

  // Sync canvas, block base URL, and checkpoint state when the parent passes a new asset
  useEffect(() => {
    if (!isOpen) {
      lastSessionHydrateKeyRef.current = null;
      return;
    }

    if (initialImageEditSession) {
      const sessionHydrateKey = initialImageEditSession.hydrateKey;
      if (
        sessionHydrateKey &&
        lastSessionHydrateKeyRef.current === sessionHydrateKey
      ) {
        return;
      }
      if (sessionHydrateKey) {
        lastSessionHydrateKeyRef.current = sessionHydrateKey;
      }

      const {
        initialChatMessages,
        initialResultImageUrl,
        initialImageUrl: sessionBaseUrl,
        blockBaseImageUrl: sessionBlockBaseUrl,
        checkpointPromptsHistory: sessionCheckpointHistory,
      } = initialImageEditSession;

      if (
        Array.isArray(initialChatMessages) &&
        initialChatMessages.length > 0
      ) {
        setChatMessages(initialChatMessages);
      }

      const displayUrl =
        initialResultImageUrl || sessionBaseUrl || initialImageUrl;
      if (displayUrl) {
        setImageUrl(displayUrl);
        setBlockBaseImageUrl(sessionBlockBaseUrl || displayUrl);
        setCheckpointPromptsHistory(sessionCheckpointHistory || []);
      }
      return;
    }

    if (initialImageUrl || generationData?.sourceUrl || generationData?.url) {
      const nextUrl =
        initialImageUrl || generationData?.sourceUrl || generationData?.url;
      setImageUrl(nextUrl);
      setBlockBaseImageUrl(nextUrl);
      setCheckpointPromptsHistory([]);
    }
  }, [
    isOpen,
    initialImageUrl,
    initialImageEditSession,
    setChatMessages,
    generationData?.sourceUrl,
    generationData?.url,
  ]);

  useEffect(() => {
    if (isOpen) {
      eraseSessionRef.current += 1;
      setIsSidebarOpen(true);
      setWorkMode(initialWorkMode || IMAGE_EDIT_WORK_MODE.GENERATIVE);
      setEditingTextID(null);
      exitObjectEraser();
    }
  }, [exitObjectEraser, initialWorkMode, isOpen]);

  useEffect(() => {
    if (!isObjectEraserActive) return;
    const stillOnEraserLayer =
      Boolean(selectedLayerId) &&
      selectedLayerId === objectEraserLayerIdRef.current &&
      selectedLayer &&
      getLayerType(selectedLayer) === LAYER_TYPE.IMAGE;
    if (!stillOnEraserLayer) {
      exitObjectEraser();
    }
  }, [exitObjectEraser, isObjectEraserActive, selectedLayer, selectedLayerId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    dispatch(
      startListenerMergedFonts({
        onUpdatedDataCallback: (fonts) => {
          fontFamiliesRef.current = fonts || [];
          loadFontFaces({ fonts });
        },
        onReferenceAvailable: (refs) => {
          fontListenerRefs.current = refs;
        },
      })
    );
    return () => {
      dispatch(
        stopListenerMergedFonts({ listenerRefs: fontListenerRefs.current })
      );
    };
  }, [dispatch, isOpen]);

  useEffect(() => {
    acknowledgedLayerLossForUrlRef.current = null;
  }, [imageUrl]);

  // Get brand information on mount
  useEffect(() => {
    getBrandInformation();
    // eslint-disable-next-line
  }, []);

  return (
    <ModalBasicLayout
      modalOpen={isOpen}
      onCloseModal={onRequestClose}
      fullScreen={true}
      maxWidth={false}
      fullWidth={true}
      hasTitle={false}
      ariaLabel={t(MODAL_IMAGE_EDIT_TITLE)}
      sxContent={{
        py: 0,
        px: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
      sxPaper={{
        height: "100vh",
        maxHeight: "100vh",
      }}
    >
      <ImageEditTopBar
        title={t(MODAL_IMAGE_EDIT_TITLE)}
        imageUrl={imageUrl}
        initialImageUrl={resolvedInitialImageUrl}
        onCallbackSave={
          isLayersMode ? onSaveLayeredComposition : onCallbackSave
        }
        onClose={onRequestClose}
        onCloseAfterSave={onClose}
        showSaveAlert={showSaveAlert}
        saveDisabled={isLayersMode ? !layersDirty : undefined}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        <ResponsiveSidebarContainer
          isSmallScreen={isSmallScreen}
          open={isSidebarOpen}
          onClose={toggleSidebar}
          paperWidth={isLayersMode ? layersSidebarWidth : 420}
        >
          {isSidebarOpen && (
            <ChatSidebar
              isSmallScreen={isSmallScreen}
              title={t(MODAL_IMAGE_EDIT_TITLE)}
              isProcessing={isChatProcessing}
              onSendMessage={onSendMessage}
              onToggleSidebar={toggleSidebar}
              scrollRef={chatScrollRef}
              hideHeader
              hideInput={isLayersMode}
              width={isLayersMode ? layersSidebarWidth : 420}
              resizable={isLayersMode}
              onResizePointerDown={onResizeLayersSidebar}
              headerExtra={
                <WorkModeSelector
                  selectedValue={workMode}
                  disabled={isAnalyzing}
                  onChange={onWorkModeChange}
                />
              }
            >
              {isLayersMode ? (
                <LayersSidebar
                  layers={layers}
                  selectedLayerId={selectedLayerId}
                  selectedLayerIds={selectedLayerIds}
                  onSelectLayer={onSelectLayer}
                  isAnalyzing={isAnalyzing}
                  isLoadingSnapshot={isLoadingSnapshot}
                  errorMessage={hasSnapshot ? null : errorMessage}
                  onAnalyze={hasSnapshot ? null : onAnalyzeLayers}
                  onAddLayerUrls={onAddLayerUrls}
                  onAddTextLayer={onAddTextLayer}
                  onReorderLayers={reorderLayers}
                  onRenameLayer={renameLayer}
                  onDuplicateLayer={duplicateLayer}
                  onDeleteLayer={onDeleteLayer}
                  onToggleLock={toggleLayerLock}
                  onToggleVisible={toggleLayerVisible}
                />
              ) : (
                <SingleChatMessages
                  messages={chatMessages}
                  currentImageUrl={imageUrl}
                  onImageSelect={onImageSelect}
                />
              )}
            </ChatSidebar>
          )}
        </ResponsiveSidebarContainer>

        {isLayersMode ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
            }}
          >
            <LayerCanvas
              imageUrl={imageUrl}
              initialImageUrl={resolvedInitialImageUrl}
              layers={layers}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              isAnalyzing={!hasSnapshot && isAnalyzing}
              selectedLayerId={selectedLayerId}
              selectedLayerIds={selectedLayerIds}
              editingTextID={editingTextID}
              onTextSelectionChange={onTextSelectionChange}
              selectedTextNodeID={textSelection?.nodeID || null}
              selectedTextPhrase={textSelection?.phrase || ""}
              onSelectLayer={onSelectLayer}
              onSelectLayers={onSelectLayers}
              onLayerBoxChange={updateLayerBox}
              onLayerBoxesChange={updateLayerBoxes}
              onStartTextEdit={setEditingTextID}
              onTextCommit={onTextCommit}
              isSidebarOpen={isSidebarOpen}
              onToggleSidebar={toggleSidebar}
              showCloseButton={false}
              showSaveActions={false}
              isObjectEraserActive={isObjectEraserActive}
              objectEraserBrushFraction={objectEraserBrushFraction}
              erasingLayerIds={erasingLayerIds}
              hasObjectEraserMask={hasObjectEraserMask}
              onEraseObject={onEraseObject}
              onHasObjectEraserMaskChange={setHasObjectEraserMask}
              onRequestExitObjectEraser={exitObjectEraser}
              toolbar={
                selectedLayer &&
                getLayerType(selectedLayer) === LAYER_TYPE.IMAGE ? (
                  <CanvasToolsBar aria-label={t(CANVAS_TOOLS_BAR_LABEL)}>
                    <ObjectEraserTool
                      active={isObjectEraserActive}
                      brushFraction={objectEraserBrushFraction}
                      disabled={Boolean(erasingLayerIds[selectedLayerId])}
                      loading={Boolean(erasingLayerIds[selectedLayerId])}
                      onToggle={onToggleObjectEraser}
                      onBrushFractionChange={setObjectEraserBrushFraction}
                    />
                  </CanvasToolsBar>
                ) : null
              }
            />
            <Box
              sx={{
                width: { xs: "100%", md: 320 },
                maxWidth: { xs: "100%", md: 320 },
                height: { xs: "auto", md: "100%" },
                maxHeight: { xs: "40%", md: "none" },
                flexShrink: 0,
                bgcolor: "background.paper",
                borderLeft: { xs: 0, md: "1px solid rgba(0, 0, 0, 0.12)" },
                borderTop: { xs: "1px solid rgba(0, 0, 0, 0.12)", md: 0 },
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <InstancePropertiesPanel
                selectedItem={selectedInspectorItem}
                isUploadingAsset={isUploadingAsset}
                isTextRangeSelected={isTextRangeSelected}
                selectedPhrase={selectedPhrase}
                alignOnly={selectedLayerIds.length >= 2}
                onAlign={onAlignLayers}
                onChangeField={onChangeField}
                onChangeBox={onChangeBox}
                onUploadAsset={onUploadAsset}
                onUploadInlineReplacementAsset={onUploadInlineReplacementAsset}
                inlineReplacementUrls={selectedInlineReplacementUrls}
              />
            </Box>
          </Box>
        ) : (
          <ImageCanvas
            imageUrl={imageUrl}
            initialImageUrl={resolvedInitialImageUrl}
            isEditing={isCanvasEditing}
            isSidebarOpen={isSidebarOpen}
            onToggleSidebar={toggleSidebar}
            showCloseButton={false}
            showSaveActions={false}
          />
        )}
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalImageEdit;
